import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import {
  useMainHospitalDetail,
  useMainHospitalUpdate,
} from '@/hooks/main/useMainHospital';
import useAppToast from '@/hooks/useAppToast';
import type { Hospital, HospitalFormData } from '@/pages/main/hospital/types';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import type { TreatmentReportOption } from '../types';

interface UseTreatmentReportHospitalSelectionParams {
  control: Control<TreatmentReportFormData>;
  setValue: UseFormSetValue<TreatmentReportFormData>;
  initialHospital?: { id: number; name: string } | null;
}

export const useTreatmentReportHospitalSelection = ({
  control,
  setValue,
  initialHospital = null,
}: UseTreatmentReportHospitalSelectionParams) => {
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const hospitalUpdate = useMainHospitalUpdate();
  const [isHospitalSearchOpen, setIsHospitalSearchOpen] = useState(false);
  const [isHospitalModalOpen, setIsHospitalModalOpen] = useState(false);
  const watchedHospitalId = useWatch({ control, name: 'hospital_id' });
  const watchedDoctorId = useWatch({ control, name: 'doctor_id' });
  const [modalHospital, setModalHospital] = useState<{ id: number; name: string } | null>(
    initialHospital
  );

  const { data: hospitalDetail } = useMainHospitalDetail(watchedHospitalId ?? 0);
  const hospital = hospitalDetail?.data as Hospital | undefined;

  useEffect(() => {
    if (hospital && !modalHospital) {
      setModalHospital({ id: hospital.id, name: hospital.name });
    }
  }, [hospital, modalHospital]);

  const hospitalOptions = useMemo<TreatmentReportOption[]>(() => {
    const opts: TreatmentReportOption[] = [{ value: '', label: '医療機関選択' }];
    if (modalHospital) {
      opts.push({ value: modalHospital.id, label: modalHospital.name });
    }
    return opts;
  }, [modalHospital]);

  const doctorOptions = useMemo<TreatmentReportOption[]>(() => {
    const opts: TreatmentReportOption[] = [{ value: '', label: '担当医師選択' }];
    if (hospital?.doctors?.length) {
      [...hospital.doctors]
        .sort((a, b) => b.serial_number - a.serial_number)
        .forEach(d => opts.push({ value: d.id, label: d.name }));
    }
    return opts;
  }, [hospital]);

  const autofilledHospitalRef = useRef<number | null>(null);
  useEffect(() => {
    if (!watchedHospitalId) {
      autofilledHospitalRef.current = null;
    }
  }, [watchedHospitalId]);

  useEffect(() => {
    if (!hospital) return;
    const sortedDoctors = [...(hospital.doctors ?? [])].sort(
      (a, b) => b.serial_number - a.serial_number
    );
    if (watchedDoctorId != null) {
      const stillExists = sortedDoctors.some(d => d.id === watchedDoctorId);
      if (!stillExists) {
        setValue('doctor_id', null, { shouldDirty: true });
      }
      autofilledHospitalRef.current = hospital.id;
      return;
    }
    if (autofilledHospitalRef.current === hospital.id) return;
    if (sortedDoctors.length > 0) {
      setValue('doctor_id', sortedDoctors[0].id, { shouldDirty: true });
    }
    autofilledHospitalRef.current = hospital.id;
  }, [hospital, watchedDoctorId, setValue]);

  const handleHospitalSelect = useCallback(
    (h: Hospital) => {
      setValue('hospital_id', h.id, { shouldDirty: true });
      setModalHospital({ id: h.id, name: h.name });
      setValue('doctor_id', null, { shouldDirty: true });
      setIsHospitalSearchOpen(false);
    },
    [setValue]
  );

  const handleHospitalDelete = useCallback(
    (h: Hospital) => {
      queryClient.removeQueries({
        predicate: q =>
          q.queryKey[0] === 'hospitals' &&
          q.queryKey.includes('detail') &&
          q.queryKey.includes(h.id),
      });
      if (watchedHospitalId && h.id === watchedHospitalId) {
        setValue('hospital_id', null, { shouldDirty: true });
        setValue('doctor_id', null, { shouldDirty: true });
        setModalHospital(null);
      }
    },
    [watchedHospitalId, setValue, queryClient]
  );

  const handleHospitalSave = useCallback(
    async (data: HospitalFormData) => {
      if (watchedHospitalId) {
        await hospitalUpdate.mutateAsync({ id: watchedHospitalId, data });
        toast.success('更新が正常に終了しました。');
      }
      setIsHospitalModalOpen(false);
    },
    [hospitalUpdate, toast, watchedHospitalId]
  );

  return {
    doctorOptions,
    handleHospitalDelete,
    handleHospitalSave,
    handleHospitalSelect,
    hospital,
    hospitalOptions,
    isHospitalModalOpen,
    isHospitalSearchOpen,
    setIsHospitalModalOpen,
    setIsHospitalSearchOpen,
    watchedHospitalId,
  };
};

